// src/pages/NetworkAlerts/index.js — "Problema Geral": alertas de falha por área (Módulo C)
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";

import Button from "@material-ui/core/Button";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import AddAlertOutlinedIcon from "@material-ui/icons/AddAlertOutlined";

import MainContainer from "../../components/MainContainer";
import ConfirmationModal from "../../components/ConfirmationModal";
import { PageHeader, StatusBadge } from "../../components/ui";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";

import useStyles from "./styles";
import SearchTab from "./SearchTab";
import AlertsTab from "./AlertsTab";
import AlertFormModal from "./AlertFormModal";
import BroadcastModal from "./BroadcastModal";
import { isErpIntegration, isWhatsappConnection } from "./constants";

const TAB_SEARCH = 0;
const TAB_ALERTS = 1;

const NetworkAlerts = () => {
  const classes = useStyles();

  const [tab, setTab] = useState(TAB_SEARCH);
  const [erps, setErps] = useState([]);
  const [whatsapps, setWhatsapps] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loadingAlerts, setLoadingAlerts] = useState(false);
  const [filter, setFilter] = useState("active");

  // { alert?, prefill? } — prefill.broadcastAfter abre o disparo depois de salvar
  const [formState, setFormState] = useState(null);
  const [broadcastAlert, setBroadcastAlert] = useState(null);
  const [confirm, setConfirm] = useState(null); // { kind: 'close'|'delete', alert }

  const fetchAlerts = useCallback(async (status) => {
    setLoadingAlerts(true);
    try {
      const { data } = await api.get("/network-alerts", { params: { status } });
      setAlerts(Array.isArray(data) ? data : []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingAlerts(false);
    }
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await api.get("/whatsapp");
        if (active) {
          setWhatsapps((Array.isArray(data) ? data : []).filter(isWhatsappConnection));
        }
      } catch (err) {
        toastError(err);
      }
      try {
        // Lista completa das integrações SGP/IXC (GET /queueIntegration é paginado).
        const { data } = await api.get("/isp/erp-integrations");
        if (active) setErps((Array.isArray(data) ? data : []).filter(isErpIntegration));
      } catch (err) {
        try {
          const { data } = await api.get("/queueIntegration");
          const list = Array.isArray(data?.queueIntegrations) ? data.queueIntegrations : [];
          if (active) setErps(list.filter(isErpIntegration));
        } catch (err2) {
          toastError(err2);
        }
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    fetchAlerts(filter);
  }, [filter, fetchAlerts]);

  const activeCount = alerts.filter((a) => a.active).length;

  const openNew = () => setFormState({ prefill: null });
  const openFromSearch = (prefill, broadcastAfter = false) => {
    if (!prefill) return;
    setFormState({ prefill: { ...prefill, broadcastAfter } });
  };

  const handleSaved = (saved) => {
    const broadcastAfter = !!formState?.prefill?.broadcastAfter;
    setFormState(null);
    fetchAlerts(filter);
    if (broadcastAfter && saved?.id) {
      setBroadcastAlert(saved);
    } else if (tab !== TAB_ALERTS) {
      setTab(TAB_ALERTS);
    }
  };

  const handleConfirm = async () => {
    if (!confirm) return;
    const { kind, alert } = confirm;
    try {
      if (kind === "close") {
        await api.post(`/network-alerts/${alert.id}/close`);
        toast.success(i18n.t("networkAlerts.toasts.closed"));
      } else {
        await api.delete(`/network-alerts/${alert.id}`);
        toast.success(i18n.t("networkAlerts.toasts.deleted"));
      }
      fetchAlerts(filter);
    } catch (err) {
      toastError(err);
    } finally {
      setConfirm(null);
    }
  };

  return (
    <MainContainer maxWidth>
      <ConfirmationModal
        title={
          confirm
            ? i18n.t(`networkAlerts.confirm.${confirm.kind}Title`, { title: confirm.alert.title })
            : ""
        }
        open={!!confirm}
        onClose={() => setConfirm(null)}
        onConfirm={handleConfirm}
      >
        {confirm && i18n.t(`networkAlerts.confirm.${confirm.kind}Message`)}
      </ConfirmationModal>

      {formState && (
        <AlertFormModal
          open
          alert={formState.alert}
          prefill={formState.prefill}
          erps={erps}
          onClose={() => setFormState(null)}
          onSaved={handleSaved}
        />
      )}

      <BroadcastModal
        open={!!broadcastAlert}
        alert={broadcastAlert}
        whatsapps={whatsapps}
        onClose={() => setBroadcastAlert(null)}
        onDone={() => fetchAlerts(filter)}
      />

      <PageHeader
        title={i18n.t("networkAlerts.title")}
        subtitle={i18n.t("networkAlerts.subtitle")}
        badge={
          activeCount > 0 ? (
            <StatusBadge
              color="warning"
              label={i18n.t("networkAlerts.activeBadge", { count: activeCount })}
            />
          ) : null
        }
        actions={
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddAlertOutlinedIcon />}
            onClick={openNew}
            disabled={erps.length === 0}
          >
            {i18n.t("networkAlerts.buttons.newAlert")}
          </Button>
        }
      />

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        className={classes.tabs}
        variant="scrollable"
        scrollButtons="off"
        aria-label={i18n.t("networkAlerts.title")}
      >
        <Tab label={i18n.t("networkAlerts.tabs.search")} />
        <Tab
          label={
            activeCount > 0
              ? `${i18n.t("networkAlerts.tabs.alerts")} (${activeCount})`
              : i18n.t("networkAlerts.tabs.alerts")
          }
        />
      </Tabs>

      {tab === TAB_SEARCH && (
        <SearchTab
          erps={erps}
          onCreateAlert={(prefill) => openFromSearch(prefill, false)}
          onBroadcastNow={(prefill) => openFromSearch(prefill, true)}
        />
      )}

      {tab === TAB_ALERTS && (
        <AlertsTab
          alerts={alerts}
          loading={loadingAlerts}
          filter={filter}
          onFilterChange={setFilter}
          hasErp={erps.length > 0}
          onCreate={openNew}
          onEdit={(alert) => setFormState({ alert })}
          onClose={(alert) => setConfirm({ kind: "close", alert })}
          onBroadcast={(alert) => setBroadcastAlert(alert)}
          onDelete={(alert) => setConfirm({ kind: "delete", alert })}
        />
      )}
    </MainContainer>
  );
};

export default NetworkAlerts;
